import { type DuelSnapshot, type Reaction, type Role, roleOf, type Seat } from '@xox/shared'
import { create } from 'zustand'
import { type Effect, feedback } from '../lib/feedback'
import { type ConnectionStatus, RoomSocket } from '../lib/socket'
import {
  type CommonView,
  commonActions,
  initialCommonView,
  REACTION_TTL_MS,
  reduceCommon,
} from './shared'

/** What every duel's snapshot looks like to the generic store and pages. */
export type SnapshotOf<S> = DuelSnapshot<
  { status: string; winner: Seat | null; turn: Seat; moveCount: number },
  S
>
export type AnySnapshot = SnapshotOf<{ turnSeconds: number }>

export interface DuelView<Snap extends AnySnapshot> extends CommonView {
  snapshot: Snap | null
  role: Role
  /** The intent (cell, column) sent and not yet answered; the board draws it pressed. */
  pending: number | null
}

export interface DuelStore<Snap extends AnySnapshot> extends DuelView<Snap> {
  code: string | null
  status: ConnectionStatus
  connect: (code: string) => void
  disconnect: () => void
  takeSeat: () => void
  move: (intent: number) => void
  rematch: () => void
  leave: () => void
  react: (emoji: Reaction) => void
  expireReaction: (id: string) => void
  dismissError: () => void
}

type StateMessage<Snap> = { type: 'state'; snapshot: Snap }
type AnyMessage<Snap> = { type: string } | StateMessage<Snap>

/** Pure: folds one server frame into the view. Stale snapshots (lower seq) are ignored. */
export function reduceDuelMessage<Snap extends AnySnapshot>(
  view: DuelView<Snap>,
  msg: AnyMessage<Snap>,
  now = Date.now(),
): Partial<DuelView<Snap>> {
  const common = reduceCommon(view, msg, now)
  if (common) {
    // An error also releases the pressed intent; a new reaction otherwise flows through unchanged.
    return msg.type === 'error' ? { ...common, pending: null } : common
  }
  if (msg.type !== 'state') return {}
  const { snapshot } = msg as StateMessage<Snap>
  if (view.snapshot && snapshot.seq < view.snapshot.seq) return {}
  return {
    snapshot,
    role: view.playerId ? roleOf(snapshot, view.playerId) : 'spectator',
    pending: null,
    closedReason: null,
  }
}

/** Pure: sounds/haptics every duel shares; `onMove` adds the game's own for a new move. */
export function deriveDuelEffects<Snap extends AnySnapshot>(
  prev: DuelView<Snap>,
  next: DuelView<Snap>,
  msg: { type: string },
  onMove: (a: Snap, b: Snap) => Effect[],
): Effect[] {
  if (msg.type === 'reaction') return ['react']
  if (msg.type !== 'state') return []
  const a = prev.snapshot
  const b = next.snapshot
  if (!a || !b || b.seq <= a.seq) return []
  const out: Effect[] = []
  if (b.game.moveCount > a.game.moveCount) out.push(...onMove(a, b))
  if (a.game.status === 'playing' && b.game.status !== 'playing') {
    if (b.game.winner === null) out.push('draw')
    else out.push(next.role !== 'spectator' && b.game.winner !== next.role ? 'lose' : 'win')
  }
  const seatedNow = (['X', 'O'] as const).some((m) => a.seats[m] === null && b.seats[m] !== null)
  if (seatedNow) out.push('seat')
  return out
}

export interface DuelStoreConfig<Snap extends AnySnapshot> {
  /** WebSocket path of the game (`/ws`, `/ws/c4`). */
  wsPath: string
  /** The `move` frame for an intent. */
  moveMessage: (intent: number, expectedSeq: number) => object
  /** Client-side sanity check before sending (the server still decides). */
  canMove: (snapshot: Snap, intent: number) => boolean
  /** Game-specific effects for a new move (XOX adds `vanish`, Connect Four a `thud`). */
  moveEffects: (a: Snap, b: Snap) => Effect[]
}

/** One zustand store per duel: the socket, the latest snapshot, the viewer's role and intent. */
export function createDuelStore<Snap extends AnySnapshot>(cfg: DuelStoreConfig<Snap>) {
  let socket: RoomSocket | null = null
  return create<DuelStore<Snap>>((set, get) => ({
    ...initialCommonView,
    code: null,
    status: 'closed',
    snapshot: null,
    role: 'spectator',
    pending: null,

    connect(code) {
      if (socket && get().code === code) return
      socket?.close()
      set({ ...initialCommonView, code, snapshot: null, pending: null, status: 'connecting' })
      socket = new RoomSocket(
        code,
        {
          onStatus: (status) => set({ status }),
          onMessage: (msg) => {
            const prev = get()
            const patch = reduceDuelMessage<Snap>(prev, msg as AnyMessage<Snap>)
            const next = { ...prev, ...patch }
            for (const effect of deriveDuelEffects(prev, next, msg, cfg.moveEffects))
              feedback(effect)
            set(patch)
            if (msg.type === 'reaction') {
              setTimeout(() => get().expireReaction(msg.id), REACTION_TTL_MS)
            }
          },
        },
        cfg.wsPath,
      )
      socket.connect()
    },

    disconnect() {
      socket?.close()
      socket = null
      set({
        code: null,
        snapshot: null,
        status: 'closed',
        pending: null,
        role: 'spectator',
        reactions: [],
      })
    },

    ...commonActions<DuelStore<Snap>>(() => socket, set),

    move(intent) {
      const { snapshot, role, pending } = get()
      if (!snapshot || role === 'spectator' || pending !== null) return
      if (snapshot.game.turn !== role || snapshot.game.status !== 'playing') return
      if (!cfg.canMove(snapshot, intent)) return
      feedback('tap')
      set({ pending: intent })
      socket?.send(cfg.moveMessage(intent, snapshot.seq) as never)
      // If the server never answers (dropped frame), release the pressed state.
      setTimeout(() => {
        if (get().pending === intent) set({ pending: null })
      }, 3000)
    },

    rematch() {
      socket?.send({ type: 'rematch' })
    },
  }))
}

export type DuelStoreHook<Snap extends AnySnapshot> = ReturnType<typeof createDuelStore<Snap>>
